import { BaseComponentUpdatePlugin } from '../core/BasePlugin'
import { IChangeValue, IComponentUpdate, IComponentUpdateV2Payload, IComponentUpdateV2State } from '../core/types'
import Vue from 'vue'

interface ComponentUpdate {
  componentName: string
  updateCount: number
  lastUpdateTime: number
  updateDuration: number
  filePath?: string
  // Track only property change counts since we don't have values
  propertyChanges: {
    props: Record<string, number>
    data: Record<string, number>
  }
}

interface UpdateStore {
  updates: ComponentUpdate[]

}

export class StateManagerPlugin extends BaseComponentUpdatePlugin {
  public readonly id = 'component-update-state-manager'
  public enabled = true

  private store: UpdateStore
  private static timer: NodeJS.Timeout
  reRenderHistory:{
    [key: string]: IComponentUpdateV2State
  }= {}

  constructor() {
    super('component-update-state-manager', 'Component Update State Manager')
    // Make the store reactive using Vue.observable
    this.store = Vue.observable({
      updates: [],
    })
  }

  getHTMLElement(compoentName: string) {
    return this.reRenderHistory[compoentName].htmlElement
  }

  updateReRenderHistory(data: IComponentUpdateV2Payload) {
    const componentName = data.componentName
    if (this.reRenderHistory[componentName]) {
      this.reRenderHistory[componentName].changedData.push(data.changedData)
      this.reRenderHistory[componentName].changedProps.push(data.changedProps)
      this.reRenderHistory[componentName].filePath = data.filePath
      this.reRenderHistory[componentName].updateCount = data.updateCount
      this.reRenderHistory[componentName].htmlElement = data.htmlElement
      this.reRenderHistory[componentName].lastUpdateTime = data.lastUpdateTime
      this.reRenderHistory[componentName].updateDuration = data.updateDuration
    } else {
      this.reRenderHistory[componentName] = {
        ...data,
        changedProps: [data.changedProps],
        changedData: [data.changedData],
      }
    }
    if (StateManagerPlugin.timer) {
      clearTimeout(StateManagerPlugin.timer)
    }
    StateManagerPlugin.timer = setTimeout(() => {
      this.store.updates = Object.values(this.reRenderHistory).map(d => {
        return {
          componentName: d.componentName,
          lastUpdateTime: d.lastUpdateTime,
          updateCount: d.updateCount,
          updateDuration: d.updateDuration,
          filePath: d.filePath,
          propertyChanges: {
            props: this.countChangedKeys(d.changedProps),
            data: this.countChangedKeys(d.changedData),
          },
        }
      })
    }, 2000)
  }

  countChangedKeys(changes: IChangeValue[]): Record<string, number> {
    return changes.reduce((acc, change) => {
      for (const key in change.values) {
        const { changed } = change.values[key]
        if (changed) {
          acc[key] = (acc[key] || 0) + 1
        }
      }
      return acc
    }, {} as Record<string, number>)
  }

  removeComponentFromHistory(componentName: string) {
    delete this.reRenderHistory[componentName]
  }

  public getStore(): UpdateStore {
    return this.store
  }

  public onBeforeUpdate(data: any): void {
    // Handle before update if needed
  }

  public onComponentMounted(componentName: string): void {
    // Initialize the component in the store when it's mounted
    if (!this.store.updates.find(u => u.componentName === componentName)) {
      this.store.updates.push({
        componentName,
        updateCount: 0,
        lastUpdateTime: Date.now(),
        updateDuration: 0,
        // propertyChanges: {
        //   props: {},
        //   data: {}
        // }
      })
    }
  }

  public onComponentUpdated(data: IComponentUpdate[]): void {
    data.forEach(d => {
      const existingUpdate = this.store.updates.find(u => u.componentName === d.componentName)

      if (existingUpdate) {
        this.updateExistingComponent(existingUpdate, d)
      } else {
        this.initializeNewComponent(d)
      }
    })
  }

  private updateExistingComponent(existingUpdate: ComponentUpdate, data: IComponentUpdate): void {
    // Update basic component data
    Object.assign(existingUpdate, {
      updateCount: (existingUpdate.updateCount || 0) + 1,
      lastUpdateTime: data.lastUpdateTime,
      updateDuration: data.updateDuration,
      filePath: data.filePath,
    })

    // Ensure propertyChanges exists
    // if (!existingUpdate.propertyChanges) {
    //   Vue.set(existingUpdate, 'propertyChanges', { props: {}, data: {} });
    // }

    // // Update changes for both props and data
    // this.updatePropertyChanges(existingUpdate.propertyChanges.props, data.changedProps);
    // this.updatePropertyChanges(existingUpdate.propertyChanges.data, data.changedData);
  }

  private updatePropertyChanges(target: Record<string, number>, changedKeys: string[]): void {
    changedKeys.forEach(key => {
      if (!target[key]) {
        Vue.set(target, key, 0)
      }
      target[key]++
    })
  }

  private initializeNewComponent(data: IComponentUpdate): void {
    this.store.updates.push({
      componentName: data.componentName,
      updateCount: 1,
      lastUpdateTime: data.lastUpdateTime,
      updateDuration: data.updateDuration || 0,
      filePath: data.filePath,
      // propertyChanges: {
      //   props: this.createInitialPropertyChanges(data.changedProps),
      //   data: this.createInitialPropertyChanges(data.changedData)
      // }
    })
  }

  private createInitialPropertyChanges(keys: string[]): Record<string, number> {
    return keys.reduce((acc, key) => {
      acc[key] = 1
      return acc
    }, {} as Record<string, number>)
  }

  public clearUpdates() {
    // Clear the entire updates array
    this.store.updates = []
    this.reRenderHistory= {}
  }

  public disable(): void {
    this.enabled = false
  }

  public enable(): void {
    this.enabled = true
  }
}